/**
 * Generate a URL-friendly slug from song title and artist
 */
export function generateSongSlug(title: string, artist?: string): string {
  const parts = [title, artist].filter(Boolean);
  
  return parts
    .join(' ')
    .toLowerCase()
    .trim()
    // Replace special characters with space
    .replace(/[^\w\s-]/g, ' ')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Replace spaces with hyphens
    .replace(/\s/g, '-')
    // Remove multiple hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '');
}

/**
 * Extract artist name from sequences data
 * Handles various formats and returns cleaned artist name
 */
export function extractArtistFromSequence(sequence: any): string | null {
  // Check if there's an artist_name field directly
  if (sequence.artist_name) {
    return sequence.artist_name.trim();
  }
  
  // Try to extract from title if it contains " - " or " by "
  const title = sequence.title || '';
  
  // Common patterns: "Song Title - Artist Name" or "Song Title by Artist Name"
  const dashMatch = title.match(/\s-\s(.+)$/);
  if (dashMatch) {
    return dashMatch[1].trim();
  }
  
  const byMatch = title.match(/\sby\s(.+)$/i);
  if (byMatch) {
    return byMatch[1].trim();
  }
  
  return null;
}

/**
 * Clean song title by removing artist suffix if present
 */
export function cleanSongTitle(title: string): string {
  return title
    // Remove " - Artist" or " by Artist" patterns
    .replace(/\s-\s.+$/, '')
    .replace(/\sby\s.+$/i, '')
    .trim();
}

/**
 * Get the best song name from available options
 */
export function getBestSongName(songName?: string, sequenceTitle?: string): string {
  // Prefer song_name if available
  if (songName && songName.trim()) {
    return songName.trim();
  }
  
  // Fall back to cleaned sequence title
  if (sequenceTitle && sequenceTitle.trim()) {
    return cleanSongTitle(sequenceTitle.trim());
  }
  
  return 'Unknown Song';
}

/**
 * Extract artist name from string (simplified version of extractArtistFromSequence)
 */
export function extractArtistName(text: string): string | null {
  if (!text) return null;
  
  // Common patterns: "Song Title - Artist Name" or "Song Title by Artist Name"
  const dashMatch = text.match(/\s-\s(.+)$/);
  if (dashMatch) {
    return dashMatch[1].trim();
  }
  
  const byMatch = text.match(/\sby\s(.+)$/i);
  if (byMatch) {
    return byMatch[1].trim();
  }
  
  return null;
}